// Lane G — execution "Attachments" panel (X2): required vs provided for one operation of a batch,
// upload from file or camera (capture="environment" on mobile / getUserMedia on desktop). Completion of the
// step is refused server-side (409 ATTACHMENT_REQUIRED) while a required item is missing; the panel shows why.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Space, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { Camera, CheckCircle2, CircleAlert, FileText, Paperclip, RefreshCw, UploadCloud } from 'lucide-react';
import { sopApi, isOk } from '@/utils/sopApi';

const { Text } = Typography;
const KIND_COLOR = { PHOTO: 'cyan', PRINTOUT: 'geekblue', COA: 'purple', CHECKLIST_SCAN: 'green', REPORT: 'blue', FORMAT: 'gold', UPLOAD: 'default', OTHER: 'default' };

const CameraModal = ({ open, onCancel, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    setErr('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr('Camera not available in this browser — use "Take photo" on a mobile device or upload a file.');
      return undefined;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((s) => {
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch((e) => setErr(e?.message || 'Camera permission denied'));
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open]);
  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 720;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      if (blob) onCapture(new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };
  return (
    <Modal open={open} title="Capture" onCancel={onCancel} onOk={snap} okText="Capture" okButtonProps={{ disabled: !!err }} destroyOnHidden width={720}>
      {err ? <Alert type="warning" message={err} /> : <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', borderRadius: 8, background: '#000' }} />}
    </Modal>
  );
};

export const AttachmentsPanel = ({ batchNumber, operationId, onStatus, compact = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [camera, setCamera] = useState({ open: false, item: null });
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    if (!batchNumber || !operationId) return;
    setLoading(true);
    const r = await sopApi.attachmentStatus(batchNumber, operationId);
    setLoading(false);
    if (isOk(r)) {
      setStatus(r.data);
      onStatus && onStatus(r.data);
    }
  }, [batchNumber, operationId, onStatus]);
  useEffect(() => {
    load();
  }, [load]);

  const upload = async (file, item) => {
    if (item?.file_types?.length) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!item.file_types.map((t) => t.toLowerCase()).includes(ext)) {
        message.warning(`${item.label}: accepted types are ${item.file_types.join(', ')}`);
        return false;
      }
    }
    setBusy(item?.code || 'free');
    const r = await sopApi.uploadAttachment({
      file, batch_number: batchNumber, operation_id: operationId,
      kind: item?.kind && item.kind !== 'UPLOAD' ? item.kind : undefined,
      rule_code: item?.source === 'rule' ? item.code : undefined,
      requirement_id: item?.requirement_id,
    });
    setBusy('');
    if (isOk(r)) {
      message.success(`Attached as ${r.data.index_no}`);
      setStatus(r.data.status ? { ...r.data.status, sops: status?.sops } : status);
      onStatus && r.data.status && onStatus(r.data.status);
      load();
    } else message.error(r?.message || 'Upload failed');
    return false;
  };

  if (!batchNumber || !operationId) return null;
  const items = status?.items || [];
  const provided = status?.provided || [];
  const missing = status?.missing || [];

  return (
    <div style={{ border: '1px solid rgba(128,128,128,0.25)', borderRadius: 8, padding: compact ? 8 : 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Paperclip size={16} />
        <Text strong style={{ fontSize: compact ? 13 : 15 }}>Attachments</Text>
        {status && (missing.length ? <Tag color="red" icon={<CircleAlert size={12} style={{ verticalAlign: -2, marginRight: 4 }} />}>{missing.length} required missing</Tag> : <Tag color="green" icon={<CheckCircle2 size={12} style={{ verticalAlign: -2, marginRight: 4 }} />}>complete</Tag>)}
        <Button size="small" type="text" icon={<RefreshCw size={13} />} loading={loading} onClick={load} />
        <span style={{ flex: 1 }} />
        <Upload showUploadList={false} beforeUpload={(f) => upload(f, null)} multiple={false}>
          <Button size="small" icon={<UploadCloud size={13} />} loading={busy === 'free'}>Add file</Button>
        </Upload>
        <Button size="small" icon={<Camera size={13} />} onClick={() => setCamera({ open: true, item: null })}>Camera</Button>
      </div>
      {missing.length > 0 && <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="Step cannot be completed until these are attached" description={missing.join(' · ')} />}
      <Space direction="vertical" style={{ width: '100%' }} size={4}>
        {items.map((it) => (
          <div key={it.code} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '4px 6px', borderRadius: 6, background: it.ok ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)' }}>
            {it.ok ? <CheckCircle2 size={15} color="#16a34a" /> : <CircleAlert size={15} color="#dc2626" />}
            <Text style={{ fontSize: compact ? 12.5 : 14 }}>{it.label}</Text>
            <Tag color={KIND_COLOR[it.kind] || 'default'} style={{ margin: 0 }}>{it.kind}</Tag>
            <Tag style={{ margin: 0 }}>{it.have}/{it.min_count}</Tag>
            {it.when !== 'ALWAYS' && <Tooltip title={it.condition}><Tag color="orange" style={{ margin: 0 }}>{it.when}</Tag></Tooltip>}
            {it.verified_by && <Text type="secondary" style={{ fontSize: 11 }}>verify: {it.verified_by}</Text>}
            {it.sop && <Text type="secondary" style={{ fontSize: 11 }}>{it.sop}</Text>}
            <span style={{ flex: 1 }} />
            <Upload showUploadList={false} beforeUpload={(f) => upload(f, it)} accept={it.file_types?.length ? it.file_types.map((t) => `.${t}`).join(',') : undefined}>
              <Button size="small" icon={<UploadCloud size={12} />} loading={busy === it.code}>Upload</Button>
            </Upload>
            {(it.kind === 'PHOTO' || it.kind === 'PRINTOUT' || it.kind === 'CHECKLIST_SCAN' || it.kind === 'FORMAT') && (
              <>
                <Upload showUploadList={false} beforeUpload={(f) => upload(f, it)} accept="image/*" capture="environment">
                  <Button size="small" icon={<Camera size={12} />}>Take photo</Button>
                </Upload>
                <Button size="small" type="text" icon={<Camera size={12} />} onClick={() => setCamera({ open: true, item: it })} title="Use the desktop camera" />
              </>
            )}
          </div>
        ))}
        {items.length === 0 && status && <Text type="secondary" style={{ fontSize: 12 }}>No required attachment on this step — files can still be added as records.</Text>}
      </Space>
      {provided.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>Attached records</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
            {provided.map((p) => (
              <Tooltip key={p.id} title={`${p.index_no} · ${p.kind} · SHA-256 ${p.file_hash?.slice(0, 12)}… · ${p.uploaded_at}`}>
                <Tag icon={<FileText size={11} style={{ verticalAlign: -1, marginRight: 3 }} />} style={{ cursor: p.file_path ? 'pointer' : 'default' }} onClick={() => p.file_path && window.open(p.file_path, '_blank', 'noopener')}>
                  {p.index_no} {p.file_name}
                </Tag>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
      <CameraModal open={camera.open} onCancel={() => setCamera({ open: false, item: null })} onCapture={(f) => { setCamera({ open: false, item: null }); upload(f, camera.item); }} />
    </div>
  );
};

export default AttachmentsPanel;
